export const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:3001';

export class ApiError extends Error {
  public readonly status: number;
  public readonly code: string | undefined;
  public readonly body: unknown;
  constructor(status: number, message: string, code: string | undefined, body: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.body = body;
  }
}

// HM-384 cluster login PIN: handler globale 401 per centralizzare logout +
// redirect a /login quando il cookie hm_access scade. Setup da App.tsx al
// boot via setUnauthorizedHandler(...). Le route public (es. /auth/operators
// che NON necessita auth) NON triggerano handler perche' rispondono 200; il
// handler scatta solo su 401 di endpoint protetti (es. /rooms, /bookings, /auth/me).
type UnauthorizedHandler = (path: string) => void;
let onUnauthorized: UnauthorizedHandler | null = null;

export function setUnauthorizedHandler(handler: UnauthorizedHandler | null): void {
  onUnauthorized = handler;
}

const MUTATING_METHODS = new Set(['POST', 'PATCH', 'PUT', 'DELETE']);

export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  // Fix di classe (prod 2026-07-22, "Marca pulita non funziona"): mandiamo
  // sempre Content-Type: application/json, quindi una mutazione SENZA body
  // arriva a Fastify come JSON vuoto -> 400 FST_ERR_CTP_EMPTY_JSON_BODY.
  // Default '{}' su POST/PATCH/PUT/DELETE cosi' nessun bottone puo' morire
  // per un body dimenticato (era gia' successo su 7 chiamate).
  const method = (init?.method ?? 'GET').toUpperCase();
  const requestBody = init?.body ?? (MUTATING_METHODS.has(method) ? '{}' : undefined);
  const res = await fetch(`${API_URL}${path}`, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
    ...init,
    body: requestBody,
  });
  let body: unknown = null;
  const text = await res.text();
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      body = text;
    }
  }
  if (!res.ok) {
    const code =
      typeof body === 'object' && body !== null && 'error' in body
        ? String((body as { error: unknown }).error)
        : undefined;
    // 401 globale: notify handler. Skip per il login flow stesso (login/login-pin),
    // dove 401 e' il segnale legittimo di "credenziali errate" e va gestito
    // dalla UI di login senza redirigersi.
    if (res.status === 401 && onUnauthorized && !isLoginPath(path)) {
      onUnauthorized(path);
    }
    throw new ApiError(res.status, res.statusText || `HTTP ${res.status}`, code, body);
  }
  return body as T;
}

function isLoginPath(path: string): boolean {
  return path.startsWith('/auth/login') || path === '/auth/operators';
}
